/**
 * The airship's painted clutter (ADR 0033, lifted from brad-airship
 * `airship-clutter.js`): the working gear a crew leaves on deck and on the
 * jetty, drawn as pixel art in one atlas and stood up as camera-facing cards
 * (HD-2D), plus flat decals for what lies on the planks (flaked coils, tar,
 * wet patches) and a soft contact shade under every card. One mesh, one
 * draw.
 *
 * Cards pivot on their bottom edge (the painted prop's front ground contact)
 * and turn to face the camera like Brad's sprite, so a texel stays about a
 * screen texel; the card leans back into the prop's own footprint. Lighting is
 * the scene's (a standard material; the ship world adds the lamp light,
 * radiance and cloud shadow to the deck's materials). Jetty gear rides with
 * the mooring part and dissolves with it as the ship casts off.
 *
 * Changed from the prototype: the cards are the deck scene's `ship_clutter`
 * placements (the scene vocabulary for props) instead of a table in code;
 * the atlas is the deck's `clutter` texture slot (the mesh hides until it
 * arrives); the replaced chunks are extended through a `ShaderHook`.
 */
import * as THREE from 'three';
import { addShaderHook } from '../material-hooks.ts';
import { type ShipKit, tag } from './kit.ts';

/** The atlas's layout (`airship-clutter-v1.json`): its size and each sprite's [x, y, w, h] in pixels. */
export const clutterAtlas = {
  size: [256, 128],
  sprites: {
    bucketBrush: [2, 2, 29, 30],
    barrel: [33, 2, 21, 27],
    barrelOld: [56, 2, 21, 27],
    fireBuckets: [79, 2, 44, 27],
    coilBig: [125, 2, 26, 26],
    wet: [153, 2, 34, 26],
    crate: [189, 2, 26, 25],
    cratePlain: [217, 2, 26, 25],
    handcart: [2, 34, 37, 25],
    sacksFeed: [41, 34, 26, 23],
    sacksSoil: [69, 34, 26, 22],
    shade: [97, 34, 32, 20],
    coilFlat: [131, 34, 19, 19],
    seaChest: [152, 34, 25, 19],
    tarPot: [179, 34, 14, 19],
    oilCan: [195, 34, 14, 15],
    coilSmall: [211, 34, 14, 14],
    ropeCoilStand: [227, 34, 17, 14],
    tar: [2, 61, 16, 13],
    blocks: [20, 61, 13, 12],
  },
} as const satisfies { readonly size: readonly [number, number]; readonly sprites: Readonly<Record<string, readonly [number, number, number, number]>> };

/** Atlas pixels per metre (Brad: about 46 px for 1.6 m). */
const PPM = 29;
/** The soft contact shade under each upright card. */
const SHADE = 'shade';

export type ClutterItem = {
  readonly sprite: string;
  readonly x: number;
  readonly z: number;
  readonly depth: number;
  readonly lift?: number;
  readonly flip?: boolean;
  readonly jetty?: boolean;
  readonly decal?: boolean;
  readonly angle?: number;
  readonly scale?: number;
};

export type Clutter = {
  readonly mesh: THREE.Mesh;
  setAtlas(atlas: THREE.Texture): void;
  update(mooringX: number, mooringY: number, fade: number): void;
  dispose(): void;
};

type Rect = readonly [number, number, number, number];
type V3 = readonly [number, number, number];

const rect = (name: string): Rect => {
  const sprites: Readonly<Record<string, Rect>> = clutterAtlas.sprites;
  const r = sprites[name];
  if (r === undefined) throw new Error(`clutter atlas: no sprite ${name}`);
  return r;
};

const normalBody = `
  // Camera axes in world space (rows of the view rotation).
  vec3 camRight=vec3(viewMatrix[0][0],viewMatrix[1][0],viewMatrix[2][0]);
  vec3 camUp=vec3(viewMatrix[0][1],viewMatrix[1][1],viewMatrix[2][1]);
  vec3 camBack=vec3(viewMatrix[0][2],viewMatrix[1][2],viewMatrix[2][2]);
  vec3 flatBack=normalize(vec3(camBack.x,0.,camBack.z)+vec3(0.,0.,1e-4));
  vec3 flatRight=vec3(flatBack.z,0.,-flatBack.x);
  // Cards: lit as a surface facing the viewer and the sky (cask and sack tops catch the sun like the planks); decals face up.
  objectNormal=clutterFlags.y>.5?vec3(0.,1.,0.):normalize(flatBack+vec3(0.,.45,0.));`;

const placeBody = `
  transformed=position;
  if(clutterFlags.y<.5){
    // Pivot on the bottom edge; lean with the camera so texels stay square on screen.
    vec3 up=normalize(camUp-flatRight*dot(camUp,flatRight));
    transformed+=flatRight*clutterCorner.x+up*clutterCorner.y+flatBack*clutterCorner.z;
  }
  transformed+=vec3(clutterMooring.xy,0.)*clutterFlags.x;
  clutterJetty=clutterFlags.x;`;

const fadeBody = `
  // Jetty gear dissolves with the mooring part as the ship casts off.
  float clutterFade=clutterMooring.w*clutterJetty;
  if(clutterFade>0.){
    mat4 bayer=mat4(0.,8.,2.,10.,12.,4.,14.,6.,3.,11.,1.,9.,15.,7.,13.,5.);
    if(clutterFade>(bayer[int(mod(gl_FragCoord.x,4.))][int(mod(gl_FragCoord.y,4.))]+.5)/16.)discard;
  }`;

export const createClutter = (_kit: ShipKit, items: readonly ClutterItem[]): Clutter => {
  const [AW, AH] = clutterAtlas.size;
  const pos: number[] = [];
  const corner: number[] = [];
  const uv: number[] = [];
  const flags: number[] = [];
  const index: number[] = [];
  const quad = (p: readonly V3[], corners: readonly V3[], [x, y, w, h]: Rect, flip: boolean, flag: readonly [number, number]): void => {
    const base = pos.length / 3;
    const u0 = x / AW;
    const u1 = (x + w) / AW;
    const v0 = 1 - (y + h) / AH;
    const v1 = 1 - y / AH;
    // `flip` mirrors the card left to right.
    const [ua, ub] = flip ? [u1, u0] : [u0, u1];
    const uvs: readonly (readonly [number, number])[] = [
      [ua, v0],
      [ub, v0],
      [ub, v1],
      [ua, v1],
    ];
    for (let i = 0; i < 4; i += 1) {
      const pi = p[i] ?? [0, 0, 0];
      const ci = corners[i] ?? [0, 0, 0];
      const ti = uvs[i] ?? [0, 0];
      pos.push(...pi);
      corner.push(...ci);
      uv.push(...ti);
      flags.push(...flag);
    }
    index.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  const decal = (name: string, x: number, z: number, rot: number, jetty: boolean, scale: number): void => {
    const r = rect(name);
    const hw = (r[2] / PPM / 2) * scale;
    const hd = (r[3] / PPM / 2) * scale;
    const c = Math.cos(rot);
    const s = Math.sin(rot);
    const y = jetty ? 0.035 : 0.012;
    const pt = (a: number, b: number): V3 => [x + a * c - b * s, y, z + a * s + b * c];
    const zero: V3 = [0, 0, 0];
    // Flat quads: the position is final and the corner unused; flag.y 1 marks "flat".
    quad([pt(-hw, hd), pt(hw, hd), pt(hw, -hd), pt(-hw, -hd)], [zero, zero, zero, zero], r, false, [jetty ? 1 : 0, 1]);
  };
  const cards = items.filter((i) => i.decal !== true);
  const decals = items.filter((i) => i.decal === true);
  const shadeWidth = rect(SHADE)[2] / PPM;
  // Shades first so they draw under the coils and cards (one transparent mesh).
  for (const o of cards) {
    const w = rect(o.sprite)[2] / PPM;
    decal(SHADE, o.x, o.z, 0, o.jetty === true, Math.max(0.5, (w * 1.05) / shadeWidth));
  }
  for (const o of decals) decal(o.sprite, o.x, o.z, o.angle ?? 0, o.jetty === true, o.scale ?? 1);
  for (const o of cards) {
    const r = rect(o.sprite);
    const w = r[2] / PPM;
    const h = r[3] / PPM;
    const y = o.lift ?? (o.jetty === true ? 0.03 : 0);
    const p: V3 = [o.x, y, o.z];
    // The corner: right offset, up offset, forward depth toward the camera.
    quad(
      [p, p, p, p],
      [
        [-w / 2, 0, o.depth],
        [w / 2, 0, o.depth],
        [w / 2, h, o.depth],
        [-w / 2, h, o.depth],
      ],
      r,
      o.flip === true,
      [o.jetty === true ? 1 : 0, 0],
    );
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('clutterCorner', new THREE.Float32BufferAttribute(corner, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('clutterFlags', new THREE.Float32BufferAttribute(flags, 2));
  g.setIndex(index);
  tag(g);

  const uniforms = { clutterMooring: { value: new THREE.Vector4(0, 0, 0, 0) } };
  const material = new THREE.MeshStandardMaterial({
    transparent: true,
    alphaTest: 0.04,
    roughness: 0.95,
    metalness: 0,
    side: THREE.DoubleSide,
    // One pass: transparent and double-sided would draw twice.
    forceSinglePass: true,
  });
  material.name = 'airship-clutter';
  addShaderHook(material, {
    key: 'ship-clutter',
    uniforms,
    vertexHeader: 'attribute vec3 clutterCorner;attribute vec2 clutterFlags;uniform vec4 clutterMooring;varying float clutterJetty;',
    fragmentHeader: 'uniform vec4 clutterMooring;varying float clutterJetty;',
    vertex: [
      { chunk: 'beginnormal_vertex', after: normalBody },
      { chunk: 'begin_vertex', after: placeBody },
    ],
    fragment: [{ chunk: 'alphatest_fragment', after: fadeBody }],
  });
  const mesh = new THREE.Mesh(g, material);
  mesh.name = 'Clutter cards';
  // Vertices move in the shader.
  mesh.frustumCulled = false;
  mesh.receiveShadow = true;
  mesh.castShadow = false;
  mesh.renderOrder = 1;
  mesh.visible = false;
  return {
    mesh,
    setAtlas(atlas) {
      material.map = atlas;
      material.needsUpdate = true;
      mesh.visible = true;
    },
    update(mooringX, mooringY, fade) {
      // Follow the mooring part exactly (the flight moves and fades it).
      uniforms.clutterMooring.value.set(mooringX, mooringY, 0, fade);
    },
    dispose() {
      g.dispose();
      material.dispose();
    },
  };
};
